import type { GaApp } from '~/modules/app'
import type {
  ApiPrizeModel,
  PrizeImageField,
  PrizeImagesData,
  PrizeModel,
} from '~/modules/main'

import { VIEW_PRIZES_LENGTH } from '../constants'

export class PrizesService {
  gaApp: GaApp

  constructor(gaApp: GaApp) {
    this.gaApp = gaApp
  }

  /**
   * Получение призов для рулетки и страницы призов
   */
  async getPrizes(): Promise<ApiPrizeModel[] | []> {
    try {
      return await this.gaApp.services.main.api.getPrizes()
    } catch (error) {
      this.gaApp.services.app.rejection.log({
        name: 'main:get-all-prizes',
        message: 'Ошибка получения массива призов',
        errorData: error,
      })

      return []
    }
  }

  /**
   * Удаляем скрытые призы пользователя из всех призов, полученных в ручке "available-prizes"
   */
  removeForbiddenPrizes(): PrizeModel[] {
    const roulettePrizes = this.gaApp.stores.main.main.allRoulettePrizes
    const forbiddenPrizesId =
      this.gaApp.stores.main.user.data?.forbiddenPrizes || []

    return roulettePrizes.filter(
      (prize: PrizeModel) => !forbiddenPrizesId.includes(prize.id),
    )
  }

  /**
   * Первые до VIEW_PRIZES_LENGTH призов из allRoulettePrizes (порядок как в API).
   */
  prepareRoulettePrizes(): PrizeModel[] {
    const availablePrizes = this.gaApp.stores.main.main.allRoulettePrizes
    const prizesLength = availablePrizes.length
    if (!prizesLength) return []

    const maxLength =
      prizesLength > VIEW_PRIZES_LENGTH ? VIEW_PRIZES_LENGTH : prizesLength

    return availablePrizes.slice(0, maxLength)
  }

  /**
   * Закэшированная обрезка для рулетки; один раз заполняется prepareRoulettePrizes.
   */
  getCroppedRoulettePrizes(): PrizeModel[] {
    if (!this.gaApp.stores.main.user.isAuthorized) return []

    const main = this.gaApp.stores.main.main

    if (!main.croppedRoulettePrizes.length && main.allRoulettePrizes.length) {
      main.croppedRoulettePrizes = this.prepareRoulettePrizes()
    }

    return main.croppedRoulettePrizes
  }

  /**
   * Загрузка призов и их изображений
   */
  async loadPrizes() {
    try {
      const prizes = await this.getPrizes()

      if (!prizes.length) return

      // На холодном старте (инкогнито/без кэша) массовая префетч-загрузка всех изображений
      // (особенно modalImage) забивает сеть и откладывает реально нужные в моменте ресурсы.
      // Догружаем "тяжёлые" поля по месту использования (например, при открытии модалки).
      const imageFields: PrizeImageField[] = ['largeImage', 'smallImage']

      const prizesWithImages: PrizeModel[] | [] = []

      let loadedCount = 0

      prizes.forEach((prize: ApiPrizeModel, index: number) => {
        let imagesToLoad = 0
        let imagesLoaded = 0

        const imagesData: PrizeImagesData = {
          largeImage: '',
          smallImage: '',
          cardImage: '',
          modalImage: '',
        }

        const checkComplete = () => {
          imagesLoaded++

          if (imagesLoaded === imagesToLoad) {
            prizesWithImages[index] = {
              id: prize.id,
              expirationDate: prize?.expirationDate ?? '',
              expirationDateText: prize?.expirationDateText ?? '',
              isExpired: prize?.isExpired ?? false,
              isRatingReward: prize?.isRatingReward ?? false,
              code: prize?.code ?? '',
              state: prize?.state ?? 'Issued',
              prizeDescription: {
                id: prize?.id ?? '',
                shortName: prize?.shortName ?? '',
                fullName: prize?.fullName ?? '',
                shortDescription: prize?.shortDescription ?? '',
                shortDescriptionUsersPrize:
                  prize?.shortDescriptionUsersPrize ?? '',
                fullDescription: prize?.fullDescription ?? '',
                type: prize?.type ?? null,
                sortOrder: prize?.sortOrder,
                images: imagesData,
                animation: prize?.animation ?? '',
                products: prize?.products ?? [],
                productsLink: prize?.productsLink ?? '',
                shouldEnrichProducts: prize?.shouldEnrichProducts,
                supplements: prize?.supplements ?? false,
                termsLink: prize?.termsLink ?? '',
              },
            }

            loadedCount++
            if (loadedCount === prizes.length) {
              this.gaApp.stores.main.main.allRoulettePrizes = prizesWithImages
            }
          }
        }

        imageFields.forEach((field: PrizeImageField) => {
          if (prize.images[field]) {
            imagesToLoad++

            this.gaApp.services.app.sourceLoader.loadImage(
              prize.images[field],
              (img: HTMLImageElement) => {
                imagesData[field] = img.src

                checkComplete()
              },
            )
          }
        })
      })
    } catch (error) {
      this.gaApp.services.app.rejection.log({
        name: 'main:prizes-images-loading',
        message: 'Ошибка загрузки изображений призов',
        errorData: error,
      })
    }
  }

  /**
   * Загрузка призов рулетки и добавление в стор без подгрузки изображений
   */
  async loadRoulettePrizes() {
    try {
      const prizes = await this.getPrizes()

      this.gaApp.stores.main.main.allRoulettePrizes = prizes.map(
        (prize: ApiPrizeModel) => ({
          id: prize.id,
          expirationDate: prize?.expirationDate ?? '',
          expirationDateText: prize?.expirationDateText ?? '',
          isExpired: prize?.isExpired ?? false,
          isRatingReward: prize?.isRatingReward ?? false,
          code: prize?.code ?? '',
          state: prize?.state ?? 'Issued',
          prizeDescription: {
            id: prize?.id ?? '',
            shortName: prize?.shortName ?? '',
            fullName: prize?.fullName ?? '',
            shortDescription: prize?.shortDescription ?? '',
            fullDescription: prize?.fullDescription ?? '',
            type: prize?.type ?? null,
            sortOrder: prize?.sortOrder,
            images: {
              largeImage: prize?.images?.largeImage ?? '',
              smallImage: prize?.images?.smallImage ?? '',
              cardImage: prize?.images?.cardImage ?? '',
              modalImage: prize?.images?.modalImage ?? '',
            },
            animation: prize?.animation ?? '',
            products: prize?.products ?? [],
            productsLink: prize?.productsLink ?? '',
            shouldEnrichProducts: prize?.shouldEnrichProducts,
            supplements: prize?.supplements ?? false,
            termsLink: prize?.termsLink ?? '',
          },
        }),
      )
    } catch (error) {
      this.gaApp.services.app.rejection.log({
        name: 'main:load-roulette-prizes-store',
        message: 'Ошибка добавления массива призов в стор',
        errorData: error,
      })
    }
  }
}
